import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  #motion = null;
  #targetOpen = null;
  #productMotion = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    // Reconnecting an existing section needs fresh listeners.
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    this.#productMotion = (this.matches('#MainContent .accordion accordion-custom') || (this.closest('header-drawer') && this.details.id === 'Details-menu-drawer-collections')) &&
      typeof this.details.animate === 'function';
    this.toggleAttribute('data-harven-product-motion', this.#productMotion);
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.#reducedMotion.addEventListener('change', this.#handleMotionPreference, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#finishMotion();
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Leave other menus, filters and footer accordions on Horizon's original path.
    if (!this.#productMotion || event.defaultPrevented) return;
    event.preventDefault();
    this.#animateTo(!(this.#targetOpen ?? this.details.open));
  };

  #handleMotionPreference = () => {
    if (this.#reducedMotion.matches) this.#finishMotion();
  };

  #finishMotion() {
    const animation = this.#motion;
    this.#motion = null;
    if (animation) {
      animation.onfinish = null;
      animation.cancel();
    }
    if (this.#targetOpen !== null) this.details.open = this.#targetOpen;
    this.#targetOpen = null;
    this.details.removeAttribute('data-harven-animating');
    this.summary.removeAttribute('aria-expanded');
  }

  #animateTo(open) {
    const details = this.details;
    // Read the current rendered size before cancelling, so repeated taps reverse smoothly.
    const from = details.getBoundingClientRect().height;
    const previous = this.#motion;
    this.#motion = null;
    if (previous) {
      previous.onfinish = null;
      previous.cancel();
    }
    this.#targetOpen = open;
    if (!open && details.querySelector('.details-content')?.contains(document.activeElement)) {
      this.summary.focus({ preventScroll: true });
    }
    if (this.#reducedMotion.matches) {
      this.#finishMotion();
      return;
    }

    // Measure both real endpoints; no maximum-height cap or auto-size interpolation.
    details.removeAttribute('data-harven-animating');
    let to;
    if (!open && this.closest('header-drawer') && details.id === 'Details-menu-drawer-collections') {
      const box = getComputedStyle(details);
      const heading = getComputedStyle(this.summary);
      const px = value => parseFloat(value) || 0;
      to = this.summary.getBoundingClientRect().height +
        px(heading.marginTop) + px(heading.marginBottom) +
        px(box.paddingTop) + px(box.paddingBottom) +
        px(box.borderTopWidth) + px(box.borderBottomWidth);
    } else {
      details.open = open;
      to = details.getBoundingClientRect().height;
    }
    details.open = true;
    details.setAttribute('data-harven-animating', '');
    this.summary.setAttribute('aria-expanded', String(open));

    if (Math.abs(from - to) < 0.5) {
      this.#finishMotion();
      return;
    }

    const animation = details.animate(
      [{ height: from + 'px' }, { height: to + 'px' }],
      {
        duration: open ? 450 : 240,
        easing: open ? 'cubic-bezier(.4, 0, .2, 1)' : 'cubic-bezier(.4, 0, 1, 1)',
        fill: 'both',
      }
    );
    this.#motion = animation;
    animation.onfinish = () => {
      if (this.#motion === animation) this.#finishMotion();
    };
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    this.#finishMotion();
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.#finishMotion();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
